import styles from './spending.module.css';
import { Empty, Select, Typography } from 'antd';
import {
  Chart,
  Filler,
  Legend,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip,
} from 'chart.js';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Radar } from 'react-chartjs-2';
import { AppContext } from '../../../../../App';
import {
  getAvailableMonthsFromTop5Data,
  getCurrentShortMonthYear,
} from '../../../../../Utils/utils';
import { ReactComponent as NoTxn } from '../../../../../Assets/noTxn.svg';
import { color2, color6_20 } from '../../constants';
import { getCategoryChartDataForMonth } from '../../../utils/savingsUtils';

const wrapLabel = (text: string): string[] => text.split(' ');

const SMALL_SCREEN_BREAKPOINT = 380;
const VALUE_FONT = { family: 'Roboto', size: 16, weight: 700 };
const NAME_FONT = { family: 'Roboto', size: 12, weight: 500 };
const VALUE_FONT_SMALL = { family: 'Roboto', size: 10, weight: 700 };
const NAME_FONT_SMALL = { family: 'Roboto', size: 8, weight: 500 };
const VALUE_COLOR = '#1A3255';
const NAME_COLOR = '#181D27';

const getChartOptions = (isSmallScreen: boolean) => {
  const layoutFont = isSmallScreen ? VALUE_FONT_SMALL : VALUE_FONT;
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    layout: {
      padding: isSmallScreen ? 2 : 8,
    },
    scales: {
      r: {
        ticks: {
          font: {
            size: isSmallScreen ? 7 : 9,
            weight: 500,
          },
        },
        pointLabels: {
          color: 'transparent',
          font: {
            family: 'Roboto',
            size: layoutFont.size,
            weight: layoutFont.weight,
          },
          padding: isSmallScreen ? 4 : 8,
        },
      },
    },
    elements: {
      point: {
        radius: 0,
        hoverRadius: 0,
        hitRadius: 0,
      },
    },
  };
};

// Chart.js's pointLabels renderer applies a single font/color to every line
// of a label, so the "32%" line and the category-name line(s) below it can't
// be styled differently via options. This plugin leaves Chart.js's own
// pointLabels layout/sizing untouched (so spacing stays correct) but draws
// them invisibly (see `color: 'transparent'` below), then redraws each
// label itself with the value line and name line(s) styled independently.
const twoTonePointLabelsPlugin = {
  id: 'twoTonePointLabels',
  afterDraw(chart: any) {
    const scale = chart.scales?.r;
    const labels = chart.data.labels as string[][] | undefined;
    if (!scale || !labels) return;

    const isSmallScreen = window.innerWidth < SMALL_SCREEN_BREAKPOINT;
    const valueFont = isSmallScreen ? VALUE_FONT_SMALL : VALUE_FONT;
    const nameFont = isSmallScreen ? NAME_FONT_SMALL : NAME_FONT;
    const valueLineHeight = valueFont.size * 1.2;
    const nameLineHeight = nameFont.size * 1.2;

    const { ctx } = chart;
    ctx.save();
    ctx.textBaseline = 'middle';

    labels.forEach((lines, index) => {
      const box = scale.getPointLabelPosition(index);
      if (!box) return;
      const [valueLine, ...nameLines] = lines;
      const centerX = (box.left + box.right) / 2;
      const threshold = 2;
      let textAlign: CanvasTextAlign = 'center';
      let x = centerX;
      if (centerX < scale.xCenter - threshold) {
        textAlign = 'right';
        x = box.right;
      } else if (centerX > scale.xCenter + threshold) {
        textAlign = 'left';
        x = box.left;
      }
      ctx.textAlign = textAlign;

      ctx.font = `${valueFont.weight} ${valueFont.size}px ${valueFont.family}`;
      ctx.fillStyle = VALUE_COLOR;
      ctx.fillText(valueLine, x, box.top + valueLineHeight / 2);

      ctx.font = `${nameFont.weight} ${nameFont.size}px ${nameFont.family}`;
      ctx.fillStyle = NAME_COLOR;
      let y = box.top + valueLineHeight + nameLineHeight / 2;
      nameLines.forEach((line) => {
        ctx.fillText(line, x, y);
        y += nameLineHeight;
      });
    });

    ctx.restore();
  },
};

const Spending = () => {
  const { Text } = Typography;
  const { isAccountDetailsFetched, data }: any = useContext(AppContext);
  const primary = color2;
  const primary_400_20 = color6_20;
  const top5Data = data.current.TOP5FUNDTRANSFER?.overall?.DEBIT?.monthly;
  const [categpriesData, setCategpriesData] = useState<any[]>([]);
  const [isSmallScreen, setIsSmallScreen] = useState(
    () => window.innerWidth < SMALL_SCREEN_BREAKPOINT
  );

  useEffect(() => {
    const handleResize = () =>
      setIsSmallScreen(window.innerWidth < SMALL_SCREEN_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const options = useMemo(
    () => getChartOptions(isSmallScreen),
    [isSmallScreen]
  );

  const availableMonths = useMemo(
    () =>
      getAvailableMonthsFromTop5Data(
        data.current.TOP5FUNDTRANSFER?.overall,
        ['DEBIT'],
        'desc'
      ),
    [data.current.TOP5FUNDTRANSFER]
  );

  const graphData = {
    labels: categpriesData?.map(({ percentage, name }) => [
      `${percentage}%`,
      ...wrapLabel(name),
    ]),
    datasets: [
      {
        label: 'Expenses',
        data: categpriesData?.map(({ percentage }) => percentage),
        backgroundColor: primary_400_20,
        borderColor: primary,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 0,
        pointHitRadius: 0,
      },
    ],
  };

  const getIntialData = useCallback(() => {
    const currentMonthYear = `${getCurrentShortMonthYear().slice(0, -4)}${getCurrentShortMonthYear().slice(-2)}`;
    setCategpriesData(
      getCategoryChartDataForMonth(top5Data, currentMonthYear) ?? []
    );
  }, [top5Data]);

  const onMonthChange = (value: string) => {
    setCategpriesData(getCategoryChartDataForMonth(top5Data, value) ?? []);
  };

  useEffect(() => {
    if (
      isAccountDetailsFetched &&
      data.current?.ACCOUNT_DETAILS?.accountProfileList
    ) {
      getIntialData();
    }
  }, [
    isAccountDetailsFetched,
    data.current.ACCOUNT_DETAILS,
    data,
    getIntialData,
  ]);

  Chart.register(
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend
  );
  
  return (
    <div className={styles.container}>
      <div className={styles.spendingHeaderLayout}>
        <Text className={styles.spendingTitle}>Spending Overview</Text>
        <Select
          defaultValue={getCurrentShortMonthYear()}
          onChange={(value) => onMonthChange(value)}
          options={availableMonths.map(({ monthShortForm, year }: any) => ({
            value: `${monthShortForm}-${year.toString().slice(-2)}`,
            label: `${monthShortForm} ${year}`,
          }))}
          className={styles.selectMonth}
        />
      </div>
      <div className={styles.graph}>
        {categpriesData?.length && graphData?.labels?.length > 2 ? (
          <Radar
            data={graphData}
            options={options}
            plugins={[twoTonePointLabelsPlugin]}
          />
        ) : (
          <div className={styles.emptyContainer}>
            <Empty
              className={styles.emptyState}
              image={<NoTxn />}
              description={
                <Typography.Text className={styles.emptyText}>
                  This month's spending is not recorded yet.
                </Typography.Text>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Spending;
